import { createApi } from '@reduxjs/toolkit/query/react';
import type { TableResponse, PaginatedResponse } from 'src/types/common';
import type { IPayment, IAIPaymentSuggestion } from 'src/types/payments';
import { PaymentStatus } from 'src/types/payments';
import { withUtcIsoDateRangeState } from 'src/utils/date-picker';
import type { IPaginationState } from 'src/utils/server-side-pagination';
import { convertObjToUrl } from 'src/utils/server-side-pagination';

import { authQuery } from './baseQuery';

export type CreatePayoutPayload = {
  currency: string;
  clientId: string;
  type: 'client' | 'service';
  periodEnd: string;
};

export type GetPayoutsPayload = {
  clientId: string;
  currency?: string;
  type?: 'client' | 'service';
  confirmedOn?: string | null;
};
export type UpdatePayoutPayload = {
  currency?: string;
  type?: 'client' | 'service';
  total?: string;
  confirmedOn?: string;
  clientId?: string;
};

export type IPayout = {
  id: string;
  clientId: string;
  currency: string;
  type: 'client' | 'service';
  total: string | null;
  periodStart: string;
  periodEnd: string;
  confirmedOn: string | null;
  createdAt?: string;
  updatedAt?: string;
  client?: {
    id: number;
    name: string;
    slug: string;
  };
};

export type IPayoutTransaction = {
  transactionId: string;
  createdAt: string;
  invoiceId?: string;
  amount: number;
  currency: string;
  type: 'client' | 'service' | null;
  transactionType: 'payment_received' | 'payout';
  paymentReference?: string;
  transactionReference?: string;
  paymentId?: string;
  payoutId?: string;
  invoiceItemType?: 'Capital' | 'Fee' | 'Interest' | 'ServiceFee' | 'ServiceInterest';
  stage?: string;
  paymentTransactionType?: string;
};

type GetPaymentsQueryArgs = {
  state: IPaginationState;
  limit: number;
};

type UpdateStatusPaymentPayload = Pick<IPayment, 'id' | 'status' | 'invoiceNo'>;

type PayoutApiResponse = {
  payout: IPayout;
};

const maxAttempts = 10;
const intervalMs = 1000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getPaymentsQueryUrl = ({ state, limit }: GetPaymentsQueryArgs) =>
  `/payments?limit=${limit}${convertObjToUrl(withUtcIsoDateRangeState(state))}`;

const hasGetPaymentsQueryArgs = (
  query?: Partial<GetPaymentsQueryArgs>
): query is GetPaymentsQueryArgs => Boolean(query?.state && typeof query.limit === 'number');

const pollPaymentsWithoutUpdatingCache = async (
  queryArgs: GetPaymentsQueryArgs,
  {
    dispatch,
    getState,
    extra,
  }: {
    dispatch: any;
    getState: () => unknown;
    extra: unknown;
  }
) => {
  const abortController = new AbortController();

  return authQuery(
    getPaymentsQueryUrl(queryArgs),
    {
      signal: abortController.signal,
      abort: abortController.abort.bind(abortController),
      dispatch,
      getState,
      extra,
      endpoint: 'getPayments',
      type: 'query',
      forced: true,
    },
    undefined
  );
};

export const paymentsAPISlice = createApi({
  reducerPath: 'presentation-service-payments-api',
  tagTypes: ['Payments', 'Payouts', 'PaymentStatistics', 'Invoices', 'EventLogs'],
  baseQuery: authQuery,
  endpoints: (builder) => ({
    getAllPayments: builder.query<TableResponse<IPayment>, void>({
      query: () => '/payments?page=1&limit=5000',
      transformResponse: (res: TableResponse<IPayment>) => {
        res.rows = res.rows.map((x: any) => {
          return {
            ...x,
            customerSortProp: x.customer?.customerName || x.rawFormat?.debitorName || '',
          };
        });

        return res;
      },
      providesTags: ['Payments'],
    }),

    getPayments: builder.query<PaginatedResponse<IPayment>, GetPaymentsQueryArgs>({
      query: getPaymentsQueryUrl,
      providesTags: ['Payments'],
    }),

    exportPayments: builder.mutation<{ data: { id: string } }, { state: IPaginationState }>({
      query: ({ state }) => ({
        // limit will be ignored, but I've included it for the sake of parity
        url: `/payments/generate-export?limit=10${convertObjToUrl(withUtcIsoDateRangeState(state))}`,
        method: 'POST',
      }),
    }),

    getPayoutTransactions: builder.query<
      PaginatedResponse<IPayoutTransaction>,
      { state: IPaginationState; limit: number; clientId: string }
    >({
      query: ({ state, limit, clientId }) =>
        `/payouts/transactions/grouped?clientId=${clientId}&limit=${limit}${convertObjToUrl(state)}`,
      providesTags: ['Payouts'],
      keepUnusedDataFor: 0,
    }),

    getPaymentStatistics: builder.query<
      {
        matched: number;
        not_processed: number;
        not_matched: number;
        all: number;
      },
      void
    >({
      query: () => `/payments/payment-status-statistics`,
      providesTags: ['Payments', 'PaymentStatistics'],
    }),

    // getPaymentsByIds: builder.query<TableResponse<IPayment>, string[]>({
    //   query: (ids: string[]) => ({
    //     url: "/payments/get-payments-by-ids",
    //     method: "POST",
    //     body: { ids },
    //   }),
    //   providesTags: ["Payments"],
    // }),

    getPaymentById: builder.query<TableResponse<IPayment>, string>({
      query: (id) => ({
        url: '/payments/get-payments-by-ids',
        method: 'POST',
        body: { ids: [id] },
      }),
      providesTags: ['Payments', 'PaymentStatistics'],
    }),

    getPaymentAISuggestions: builder.query<
      PaginatedResponse<IAIPaymentSuggestion>,
      { id: string; state: IPaginationState; limit: number }
    >({
      query: ({ id, state, limit }) =>
        `/payments/${id}/ai-suggestions?limit=${limit}${convertObjToUrl(state)}`,
    }),

    updateStatusPayment: builder.mutation<
      unknown,
      { data: UpdateStatusPaymentPayload[]; currentRtkQuery?: GetPaymentsQueryArgs }
    >({
      query: ({ data, currentRtkQuery }) => ({
        url: '/payments/update-status',
        method: 'POST',
        body: {
          payments: data,
        },
      }),
      async onQueryStarted(
        { data, currentRtkQuery },
        { dispatch, getState, extra, queryFulfilled }
      ) {
        const ids = data.map((x) => x.id);

        const patch = (queryArgs: GetPaymentsQueryArgs) => {
          return dispatch(
            paymentsAPISlice.util.updateQueryData('getPayments', queryArgs, (draft) => {
              if (!draft?.data || !Array.isArray(draft.data)) return;

              const removedCount = draft.data.filter((payment: Record<string, any>) =>
                ids.includes(payment.id)
              ).length;

              draft.data = draft.data.filter(
                (payment: Record<string, any>) => !ids.includes(payment.id)
              );

              if (typeof draft.totalItems === 'number') {
                draft.totalItems = Math.max(0, draft.totalItems - removedCount);
              }
            })
          );
        };

        const patchResult = hasGetPaymentsQueryArgs(currentRtkQuery)
          ? patch(currentRtkQuery)
          : undefined;

        try {
          await queryFulfilled;

          data.forEach((x) => {
            dispatch(
              paymentsAPISlice.util.updateQueryData(
                'getPaymentById',
                x.id,
                (singlePaymentDraft) => {
                  const payment = singlePaymentDraft?.rows?.find(
                    (p: Record<string, any>) => p.id === x.id
                  );

                  if (payment) {
                    payment.status = x.status;
                    payment.invoiceNo = x.invoiceNo;
                  }
                }
              )
            );
          });

          if (hasGetPaymentsQueryArgs(currentRtkQuery)) {
            await sleep(intervalMs);

            for (let attempt = 0; attempt < maxAttempts; attempt++) {
              if (attempt > 0) {
                await sleep(intervalMs);
              }

              const result = await pollPaymentsWithoutUpdatingCache(currentRtkQuery, {
                dispatch,
                getState,
                extra,
              });

              if ('error' in result) {
                continue;
              }

              const payments = Array.isArray(result.data?.data) ? result.data.data : [];
              const stillExists = ids.some((id) =>
                payments.some((payment: Record<string, any>) => payment.id === id)
              );

              if (!stillExists) {
                dispatch(paymentsAPISlice.util.invalidateTags(['Payments', 'PaymentStatistics']));
                break;
              }
            }
          } else {
            dispatch(paymentsAPISlice.util.invalidateTags(['Payments', 'PaymentStatistics']));
          }
        } catch {
          patchResult?.undo();
        }
      },
    }),

    matchPayment: builder.mutation({
      query: (requestData) => ({
        url: '/payments/match-payment',
        method: 'POST',
        body: requestData,
      }),
      async onQueryStarted(requestData, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            paymentsAPISlice.util.updateQueryData(
              'getAllPayments',
              undefined,
              (allPaymentsDraft) => {
                const payment = allPaymentsDraft.rows.find((p) => p.id === requestData.paymentId);

                if (payment) {
                  payment.status = PaymentStatus.MatchedManual;
                }
              }
            )
          );

          dispatch(
            paymentsAPISlice.util.updateQueryData(
              'getPaymentById',
              requestData.paymentId,
              (singlePaymentDraft) => {
                const payment = singlePaymentDraft.rows.find((p) => p.id === requestData.paymentId);

                if (payment) {
                  payment.status = PaymentStatus.MatchedManual;
                }
              }
            )
          );
        } catch (error) {
          console.log('Error updating payment!', error);
        }
      },
      // invalidatesTags: ["Payments"],
    }),

    createPayout: builder.mutation<APIResponse<PayoutApiResponse>, CreatePayoutPayload>({
      query: (body) => ({
        url: '/payouts',
        method: 'POST',
        body,
      }),
      async onQueryStarted(requestData, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          if (!responseData.success) {
            throw new Error('API response was not successful');
          }
          const newPayout = responseData.result.payout;

          dispatch(
            paymentsAPISlice.util.updateQueryData(
              'getPayouts',
              { clientId: requestData.clientId },
              (draft) => {
                draft.payouts.push(newPayout);
              }
            )
          );
        } catch (error) {
          console.error('Error creating payout:', error);
        }
      },
    }),

    getPayouts: builder.query<{ payouts: IPayout[] }, GetPayoutsPayload>({
      query: (params) => {
        const queryString = Object.entries(params)
          .filter(([_, value]) => value !== undefined)
          .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
          .join('&');

        return `/payouts?${queryString}`;
      },
      providesTags: ['Payouts'],
    }),

    updatePayout: builder.mutation<
      APIResponse<PayoutApiResponse>,
      { payoutId: string; data: UpdatePayoutPayload }
    >({
      query: ({ payoutId, data }) => ({
        url: `/payouts/${payoutId}/confirm`,
        method: 'POST',
        body: data,
      }),
      async onQueryStarted({ payoutId, data }, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          if (!responseData.success) {
            throw new Error('API response was not successful');
          }
          const updatedPayout = responseData.result.payout;
          const clientId = data.clientId || updatedPayout.clientId;

          dispatch(
            paymentsAPISlice.util.updateQueryData('getPayouts', { clientId }, (draft) => {
              const index = draft.payouts.findIndex((p) => p.id === payoutId);
              if (index !== -1) {
                draft.payouts[index] = updatedPayout;
              }
            })
          );
        } catch (error) {
          console.error('Error updating payout:', error);
        }
      },
    }),

    deletePayment: builder.mutation<any, { id: string; currentRtkQuery: any }>({
      query: ({ id }) => ({
        url: `/payments/delete/${id}`,
        method: 'POST',
      }),

      async onQueryStarted({ id, currentRtkQuery }, { dispatch, queryFulfilled }) {
        const patch = () => {
          return dispatch(
            paymentsAPISlice.util.updateQueryData('getPayments', currentRtkQuery, (draft) => {
              if (!draft?.data || !Array.isArray(draft.data)) return;

              draft.data = draft.data.filter((payment: Record<string, any>) => payment.id !== id);
            })
          );
        };

        const patchResult = patch();

        try {
          await queryFulfilled;

          await sleep(intervalMs);
          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const result = await dispatch(
              paymentsAPISlice.endpoints.getPayments.initiate(currentRtkQuery, {
                forceRefetch: true,
                subscribe: false,
              })
            );

            const response = result.data;

            const payments = response?.data ?? [];
            const stillExists = payments.some((payment: Record<string, any>) => payment.id === id);

            if (stillExists) {
              patch();
            }

            if (!stillExists) {
              dispatch(paymentsAPISlice.util.invalidateTags(['PaymentStatistics']));
              break;
            }

            if (attempt > 0) {
              await sleep(intervalMs);
            }
          }
        } catch {
          patchResult?.undo();
        }
      },
    }),

    reversePayment: builder.mutation<
      unknown,
      { paymentAllocationId: string; reversalReason: string }
    >({
      query: ({ paymentAllocationId, reversalReason }) => ({
        url: '/payments/reverse-payment',
        method: 'POST',
        body: { paymentAllocationId, reversalReason },
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : ['PaymentStatistics', 'Invoices', 'EventLogs'],
    }),

    deletePayments: builder.mutation<any, { paymentsIds: string[]; currentRtkQuery: any }>({
      query: ({ paymentsIds }) => ({
        url: `/payments/delete`,
        method: 'DELETE',
        body: {
          paymentsIds,
        },
      }),

      async onQueryStarted({ paymentsIds, currentRtkQuery }, { dispatch, queryFulfilled }) {
        const patch = () => {
          return dispatch(
            paymentsAPISlice.util.updateQueryData('getPayments', currentRtkQuery, (draft) => {
              if (!draft?.data || !Array.isArray(draft.data)) return;

              draft.data = draft.data.filter(
                (payment: Record<string, any>) => !paymentsIds.includes(payment.id)
              );
            })
          );
        };

        const patchResult = patch();

        try {
          await queryFulfilled;

          await sleep(intervalMs);
          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const result = await dispatch(
              paymentsAPISlice.endpoints.getPayments.initiate(currentRtkQuery, {
                forceRefetch: true,
                subscribe: false,
              })
            );

            const response = result.data;

            const payments = response?.data ?? [];
            const stillExists = paymentsIds.some((id) =>
              payments.some((payment: Record<string, any>) => payment.id === id)
            );

            if (stillExists) {
              dispatch(
                paymentsAPISlice.util.updateQueryData('getPayments', currentRtkQuery, (draft) => {
                  if (!draft?.data || !Array.isArray(draft.data)) return;

                  draft.data = draft.data.filter(
                    (payment: Record<string, any>) => !paymentsIds.includes(payment.id)
                  );
                })
              );
            }

            if (!stillExists) {
              dispatch(paymentsAPISlice.util.invalidateTags(['Payments']));
              break;
            }

            if (attempt > 0) {
              await sleep(intervalMs);
            }
          }
        } catch {
          patchResult?.undo();
        }
      },
    }),
  }),
});

export const {
  useGetAllPaymentsQuery,
  useGetPaymentsQuery,
  useGetPayoutTransactionsQuery,
  useGetPaymentStatisticsQuery,
  // useLazyGetPaymentsByIdsQuery,
  useLazyGetPaymentByIdQuery,
  useGetPaymentByIdQuery,
  useUpdateStatusPaymentMutation,
  useMatchPaymentMutation,
  useGetPaymentAISuggestionsQuery,
  useCreatePayoutMutation,
  useGetPayoutsQuery,
  useUpdatePayoutMutation,
  useDeletePaymentMutation,
  useExportPaymentsMutation,
  useDeletePaymentsMutation,
  useReversePaymentMutation,
} = paymentsAPISlice;
